<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Calendar, Hand, Home, MapPin, Mic, Send, Smile, Sparkles, Square, X } from 'lucide-vue-next';
import MailboxCard from '@/components/MailboxCard.vue';
import { useFamily } from '@/composables/useFamily';
import type { ChildReplyKind } from '@/composables/useFamily';
import type { Message, MessageStatus, PlayRequest } from '@/types/domain';

const {
  addDirectChildReaction,
  addPendingVoiceReply,
  childFriends,
  childMailboxMessages,
  childMailboxPlayRequests,
  family,
  isChildMailboxViewer,
  loadFamilyData,
  markMessageRead,
  messageSenderInfo,
  selectedChild,
  unreadChildMessageCount
} = useFamily();
const selectedMessageId = ref('');
const selectedPlayRequestId = ref('');
const replyNotice = ref('');
const isRecordingVoice = ref(false);
const voicePreviewUrl = ref('');
const voicePreviewBlob = ref<Blob | null>(null);
const voiceError = ref('');
const voiceRecorder = ref<MediaRecorder | null>(null);
const voiceSeconds = ref(0);
let voiceChunks: BlobPart[] = [];
let voiceTimerId = 0;
const MAX_REPLY_AUDIO_SECONDS = 20;

onMounted(() => {
  void loadFamilyData(family.value.id || undefined, { force: true });
});

const selectedMessage = computed(
  () => childMailboxMessages.value.find((message) => message.id === selectedMessageId.value) ?? null
);

const selectedPlayRequest = computed(
  () => childMailboxPlayRequests.value.find((request) => request.id === selectedPlayRequestId.value) ?? null
);

const canReplyToSelectedMessage = computed(
  () =>
    Boolean(selectedMessage.value) &&
    selectedMessage.value?.senderChildId !== selectedChild.value.id &&
    (selectedMessage.value?.status === 'received' || selectedMessage.value?.status === 'approved') &&
    isChildMailboxViewer.value
);

function openMessage(message: Message) {
  selectedMessageId.value = message.id;
  selectedPlayRequestId.value = '';
  replyNotice.value = '';
  if (isChildMailboxViewer.value) {
    markMessageRead(message.id);
  }
}

function openPlayRequest(request: PlayRequest) {
  selectedPlayRequestId.value = request.id;
  selectedMessageId.value = '';
  replyNotice.value = '';
}

function closeMessage() {
  selectedMessageId.value = '';
  selectedPlayRequestId.value = '';
  replyNotice.value = '';
  resetVoiceReply();
}

function messageStatusLabel(status: MessageStatus) {
  if (status === 'pending') return 'wartet auf Freigabe';
  if (status === 'approved') return 'freigegeben';
  if (status === 'rejected') return 'abgelehnt';
  return 'empfangen';
}

function playRequestStatusLabel(status: PlayRequest['status']) {
  if (status === 'pending_parent_approval') return 'wartet auf Freigabe';
  if (status === 'sent_to_receiver_parent') return 'freigegeben';
  if (status === 'rejected_by_sender_parent' || status === 'declined' || status === 'cancelled') return 'abgelehnt';
  return 'empfangen';
}

function statusClass(status: MessageStatus | PlayRequest['status']) {
  const playStatuses: PlayRequest['status'][] = [
    'draft',
    'pending_parent_approval',
    'rejected_by_sender_parent',
    'sent_to_receiver_parent',
    'confirmed',
    'counter_proposal',
    'declined',
    'cancelled'
  ];
  const label = playStatuses.includes(status as PlayRequest['status'])
    ? playRequestStatusLabel(status as PlayRequest['status'])
    : messageStatusLabel(status as MessageStatus);
  if (label === 'wartet auf Freigabe') return 'bg-amber-100 text-amber-800';
  if (label === 'freigegeben') return 'bg-emerald-100 text-emerald-800';
  if (label === 'abgelehnt') return 'bg-rose-100 text-rose-800';
  return 'bg-sky-100 text-sky-800';
}

function playRequestFriendName(request: PlayRequest) {
  const friendChildId = request.senderChildId === selectedChild.value.id ? request.receiverChildId : request.senderChildId;
  return childFriends.value.find((friend) => friend.friendChildId === friendChildId)?.displayName ?? 'Freund';
}

function playRequestTitle(request: PlayRequest) {
  const friendName = playRequestFriendName(request);
  return request.senderChildId === selectedChild.value.id
    ? `Spielanfrage an ${friendName}`
    : `Spielanfrage von ${friendName}`;
}

function sendQuickReply(replyKind: ChildReplyKind) {
  if (!selectedMessage.value || !canReplyToSelectedMessage.value) return;

  addDirectChildReaction(selectedMessage.value.senderChildId, replyKind, selectedMessage.value.id);
  replyNotice.value = 'Deine Reaktion wurde gesendet.';
}

async function startVoiceRecording() {
  if (!selectedMessage.value || !canReplyToSelectedMessage.value) return;

  replyNotice.value = '';
  voiceError.value = '';
  resetVoiceReply();

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    voiceChunks = [];
    voiceRecorder.value = recorder;

    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) {
        voiceChunks.push(event.data);
      }
    });
    recorder.addEventListener('stop', () => {
      const audioBlob = new Blob(voiceChunks, { type: recorder.mimeType || 'audio/webm' });
      voicePreviewBlob.value = audioBlob;
      voicePreviewUrl.value = URL.createObjectURL(audioBlob);
      stream.getTracks().forEach((track) => track.stop());
      isRecordingVoice.value = false;
      voiceRecorder.value = null;
      stopVoiceTimer();
    });

    isRecordingVoice.value = true;
    recorder.start();
    startVoiceTimer();
  } catch {
    voiceError.value = 'Mikrofon konnte nicht gestartet werden.';
  }
}

function stopVoiceRecording() {
  if (!voiceRecorder.value || voiceRecorder.value.state === 'inactive') return;
  voiceRecorder.value.stop();
}

async function sendVoiceReply() {
  if (!selectedMessage.value || !canReplyToSelectedMessage.value || !voicePreviewBlob.value) return;

  await addPendingVoiceReply(selectedMessage.value.senderChildId, voicePreviewBlob.value, selectedMessage.value.id);
  replyNotice.value = 'Deine Sprachnachricht wartet auf die Elternfreigabe.';
  voicePreviewUrl.value = '';
  voicePreviewBlob.value = null;
}

function resetVoiceReply() {
  if (voiceRecorder.value && voiceRecorder.value.state !== 'inactive') {
    voiceRecorder.value.stop();
  }
  stopVoiceTimer();
  if (voicePreviewUrl.value) {
    URL.revokeObjectURL(voicePreviewUrl.value);
  }
  voicePreviewUrl.value = '';
  voicePreviewBlob.value = null;
  voiceError.value = '';
  isRecordingVoice.value = false;
  voiceSeconds.value = 0;
}

function startVoiceTimer() {
  stopVoiceTimer();
  voiceSeconds.value = 0;
  voiceTimerId = window.setInterval(() => {
    voiceSeconds.value += 1;
    if (voiceSeconds.value >= MAX_REPLY_AUDIO_SECONDS) {
      stopVoiceRecording();
    }
  }, 1000);
}

function stopVoiceTimer() {
  if (!voiceTimerId) return;
  window.clearInterval(voiceTimerId);
  voiceTimerId = 0;
}
</script>

<template>
  <section class="mx-auto max-w-5xl px-4 py-6 pb-28 sm:px-6 md:pb-10">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <h1 class="text-4xl font-black text-heart-berry md:text-6xl">{{ selectedChild.firstName }}s Briefkasten</h1>
      <span
        v-if="unreadChildMessageCount > 0"
        class="rounded-full bg-heart-berry px-5 py-3 text-lg font-black text-white shadow-sm"
      >
        {{ unreadChildMessageCount }} neu
      </span>
    </div>
    <div class="mt-8 grid gap-5">
      <div v-if="childMailboxMessages.length === 0 && childMailboxPlayRequests.length === 0" class="rounded-[28px] bg-white p-6 text-xl font-black text-heart-leaf shadow-soft">
        Noch keine Briefe angekommen.
      </div>
      <button
        v-for="message in childMailboxMessages"
        :key="message.id"
        class="focus-ring grid gap-3 rounded-[30px] bg-white/70 p-3 text-left shadow-soft transition hover:-translate-y-1 active:scale-[0.99] md:grid-cols-[1fr_auto]"
        type="button"
        :aria-label="`Brief von ${messageSenderInfo(message).name} oeffnen`"
        @click="openMessage(message)"
      >
        <MailboxCard :message="message" :sender-info="messageSenderInfo(message)" />
        <span class="inline-flex min-h-16 items-center justify-center rounded-2xl bg-heart-coral px-6 text-lg font-black text-white md:self-center">
          Ansehen
        </span>
      </button>

      <button
        v-for="request in childMailboxPlayRequests"
        :key="`play-${request.id}`"
        class="focus-ring grid gap-3 rounded-[30px] bg-white/70 p-3 text-left shadow-soft transition hover:-translate-y-1 active:scale-[0.99] md:grid-cols-[1fr_auto]"
        type="button"
        :aria-label="`${playRequestTitle(request)} oeffnen`"
        @click="openPlayRequest(request)"
      >
        <article class="rounded-[28px] border border-orange-100 bg-white p-5 shadow-soft">
          <div class="flex items-center gap-4">
            <span class="grid h-24 w-24 shrink-0 place-items-center rounded-[28px] bg-heart-mint text-heart-leaf shadow-sm md:h-28 md:w-28" aria-hidden="true">
              <Sparkles class="h-14 w-14" />
            </span>
            <div class="min-w-0 flex-1">
              <p class="text-2xl font-black text-heart-berry md:text-3xl">{{ playRequestTitle(request) }}</p>
              <div class="mt-2 flex flex-wrap items-center gap-2">
                <p class="text-lg font-black text-heart-ink">{{ request.requestedPlaceLabel }}, {{ request.requestedTimeLabel }}</p>
                <span class="rounded-full px-3 py-1 text-xs font-black uppercase" :class="statusClass(request.status)">
                  {{ playRequestStatusLabel(request.status) }}
                </span>
              </div>
              <p class="mt-3 inline-flex items-center gap-2 text-sm font-bold text-slate-500">
                <Calendar class="h-4 w-4" aria-hidden="true" />
                {{ new Date(request.createdAt).toLocaleDateString('de-DE') }}
              </p>
              <p class="mt-2 inline-flex items-center gap-2 rounded-full bg-heart-cream px-3 py-1 text-xs font-black uppercase text-heart-ink">
                <Sparkles class="h-4 w-4" aria-hidden="true" />
                Spielanfrage
              </p>
            </div>
          </div>
        </article>
        <span class="inline-flex min-h-16 items-center justify-center rounded-2xl bg-heart-coral px-6 text-lg font-black text-white md:self-center">
          Ansehen
        </span>
      </button>
    </div>

    <div v-if="selectedMessage" class="fixed inset-0 z-30 grid place-items-center bg-slate-900/45 px-4 py-6">
      <article class="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-[32px] bg-white p-5 shadow-soft md:p-7">
        <div class="flex items-start justify-between gap-4">
          <div class="flex min-w-0 items-center gap-4">
            <span
              class="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-[28px] text-white shadow-sm"
              :style="{ backgroundColor: messageSenderInfo(selectedMessage).color }"
              aria-hidden="true"
            >
              <img v-if="messageSenderInfo(selectedMessage).imageUrl" class="h-full w-full object-cover" :src="messageSenderInfo(selectedMessage).imageUrl ?? ''" alt="" />
              <Home v-else class="h-14 w-14 fill-white/40" aria-hidden="true" />
            </span>
            <div class="min-w-0">
            <p class="text-sm font-black uppercase tracking-[0.16em] text-heart-leaf">Briefinhalt</p>
              <p class="mt-1 text-3xl font-black text-heart-berry">{{ messageSenderInfo(selectedMessage).name }}</p>
              <h2 class="mt-2 text-2xl font-black text-heart-ink">{{ selectedMessage.content }}</h2>
              <p
                class="mt-3 rounded-full px-4 py-2 text-sm font-black"
                :class="statusClass(selectedMessage.status)"
              >
                {{ messageStatusLabel(selectedMessage.status) }}
              </p>
            </div>
          </div>
          <button class="focus-ring grid h-12 w-12 shrink-0 place-items-center rounded-full bg-heart-cream text-heart-ink" type="button" aria-label="Brief schliessen" @click="closeMessage">
            <X class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div v-if="selectedMessage.type === 'drawing' && selectedMessage.mediaUrl" class="mt-6 rounded-[24px] bg-heart-cream p-4">
          <img class="max-h-[36rem] w-full rounded-2xl bg-white object-contain shadow-inner" :src="selectedMessage.mediaUrl" alt="Gemaltes Bild" />
        </div>

        <div v-else-if="selectedMessage.type === 'audio' && selectedMessage.mediaUrl" class="mt-6 rounded-[24px] bg-heart-cream p-4">
          <audio class="w-full" controls :src="selectedMessage.mediaUrl" />
        </div>

        <div v-else-if="selectedMessage.type === 'video' && selectedMessage.mediaUrl" class="mt-6 rounded-[24px] bg-heart-cream p-4">
          <video class="max-h-[36rem] w-full rounded-2xl bg-black" controls :src="selectedMessage.mediaUrl" />
        </div>

        <div v-else class="mt-6 rounded-[24px] bg-heart-cream p-5 text-2xl font-black leading-10 text-heart-ink">
          {{ selectedMessage.content }}
        </div>

        <section v-if="canReplyToSelectedMessage" class="mt-6 rounded-[28px] bg-white p-4 shadow-soft">
          <h3 class="text-2xl font-black text-heart-ink">Antworten</h3>
          <div class="mt-4 grid gap-3 sm:grid-cols-3">
            <button
              class="focus-ring flex min-h-28 flex-col items-center justify-center gap-3 rounded-3xl bg-sky-100 px-4 text-center text-xl font-black text-sky-800"
              type="button"
              @click="sendQuickReply('wave')"
            >
              <Hand class="h-10 w-10" aria-hidden="true" />
              Zurueck winken
            </button>
            <button
              class="focus-ring flex min-h-28 flex-col items-center justify-center gap-3 rounded-3xl bg-heart-mint px-4 text-center text-xl font-black text-heart-leaf"
              type="button"
              @click="sendQuickReply('thanks')"
            >
              <Smile class="h-10 w-10" aria-hidden="true" />
              Danke sagen
            </button>
            <button
              v-if="!isRecordingVoice"
              class="focus-ring flex min-h-28 flex-col items-center justify-center gap-3 rounded-3xl bg-rose-100 px-4 text-center text-xl font-black text-rose-800"
              type="button"
              @click="startVoiceRecording"
            >
              <Mic class="h-10 w-10" aria-hidden="true" />
              Sprache
            </button>
            <button
              v-else
              class="focus-ring flex min-h-28 flex-col items-center justify-center gap-3 rounded-3xl bg-rose-700 px-4 text-center text-xl font-black text-white"
              type="button"
              @click="stopVoiceRecording"
            >
              <Square class="h-10 w-10" aria-hidden="true" />
              Stoppen
            </button>
          </div>
          <div v-if="voicePreviewUrl" class="mt-4 rounded-[24px] bg-heart-cream p-4">
            <audio class="w-full" controls :src="voicePreviewUrl" />
            <button
              class="focus-ring mt-4 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-heart-leaf px-5 text-lg font-black text-white"
              type="button"
              @click="sendVoiceReply"
            >
              <Send class="h-6 w-6" aria-hidden="true" />
              Sprachnachricht senden
            </button>
          </div>
          <p v-if="voiceError" class="mt-4 rounded-2xl bg-rose-100 px-4 py-3 text-center text-lg font-black text-rose-800">
            {{ voiceError }}
          </p>
          <p v-if="replyNotice" class="mt-4 rounded-2xl bg-heart-cream px-4 py-3 text-center text-lg font-black text-heart-leaf">
            {{ replyNotice }}
          </p>
        </section>
      </article>
    </div>

    <div v-if="selectedPlayRequest" class="fixed inset-0 z-30 grid place-items-center bg-slate-900/45 px-4 py-6">
      <article class="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-[32px] bg-white p-5 shadow-soft md:p-7">
        <div class="flex items-start justify-between gap-4">
          <div class="flex min-w-0 items-center gap-4">
            <span class="grid h-24 w-24 shrink-0 place-items-center rounded-[28px] bg-heart-mint text-heart-leaf shadow-sm" aria-hidden="true">
              <Sparkles class="h-14 w-14" />
            </span>
            <div class="min-w-0">
              <p class="text-sm font-black uppercase tracking-[0.16em] text-heart-leaf">Spielanfrage</p>
              <p class="mt-1 text-3xl font-black text-heart-berry">{{ playRequestTitle(selectedPlayRequest) }}</p>
              <p class="mt-3 rounded-full px-4 py-2 text-sm font-black" :class="statusClass(selectedPlayRequest.status)">
                {{ playRequestStatusLabel(selectedPlayRequest.status) }}
              </p>
            </div>
          </div>
          <button class="focus-ring grid h-12 w-12 shrink-0 place-items-center rounded-full bg-heart-cream text-heart-ink" type="button" aria-label="Spielanfrage schliessen" @click="closeMessage">
            <X class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div class="mt-6 grid gap-4 md:grid-cols-2">
          <p class="inline-flex items-center gap-3 rounded-[24px] bg-heart-cream px-4 py-4 text-xl font-black text-heart-ink">
            <MapPin class="h-7 w-7 text-heart-coral" aria-hidden="true" />
            {{ selectedPlayRequest.parentAdjustedPlace || selectedPlayRequest.requestedPlaceLabel }}
          </p>
          <p class="inline-flex items-center gap-3 rounded-[24px] bg-heart-cream px-4 py-4 text-xl font-black text-heart-ink">
            <Calendar class="h-7 w-7 text-heart-leaf" aria-hidden="true" />
            {{ selectedPlayRequest.parentAdjustedDate || selectedPlayRequest.requestedTimeLabel }}
            {{ selectedPlayRequest.parentAdjustedTimeRange }}
          </p>
        </div>

        <div class="mt-5 rounded-[24px] bg-heart-cream p-5 text-2xl font-black leading-10 text-heart-ink">
          {{ selectedPlayRequest.parentMessage || `${selectedChild.firstName} moechte spielen.` }}
        </div>
      </article>
    </div>
  </section>
</template>
